import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import { useSceneStore } from '../../store/sceneStore';
import {
  PropertyTrack,
  PROPERTY_LABELS,
} from '../../../core/types/animation.types';

interface DopeSheetProps {
  trackHeaderWidth?: number;
  className?: string;
}

const ROW_HEIGHT = 24;
const SUMMARY_ROW_HEIGHT = 28;
const DIAMOND_SIZE = 8;

interface TrackGroup {
  objectId: string;
  objectName: string;
  tracks: PropertyTrack[];
}

export const DopeSheet: React.FC<DopeSheetProps> = ({
  trackHeaderWidth = 180,
  className,
}) => {
  const {
    selectedAnimationId,
    animations,
    viewport,
    playback,
    selectedKeyframeIds,
    selectKeyframe,
    selectKeyframesInRange,
    clearKeyframeSelection,
  } = useAnimationStore();

  const { gameObjects } = useSceneStore();

  const [isBoxSelecting, setIsBoxSelecting] = useState(false);
  const [boxSelection, setBoxSelection] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const { pixelsPerSecond, scrollLeft } = viewport;
  const { currentTime } = playback;

  // Get the selected animation
  const selectedAnimation = selectedAnimationId ? animations[selectedAnimationId] : null;

  // Group tracks by target object
  const trackGroups: TrackGroup[] = useMemo(() => {
    if (!selectedAnimation) return [];

    const groupMap = new Map<string, TrackGroup>();

    selectedAnimation.tracks.forEach((track) => {
      const existing = groupMap.get(track.targetObjectId);
      const obj = gameObjects[track.targetObjectId];
      const objectName = obj?.name || 'Unknown Object';

      if (existing) {
        existing.tracks.push(track);
      } else {
        groupMap.set(track.targetObjectId, {
          objectId: track.targetObjectId,
          objectName,
          tracks: [track],
        });
      }
    });

    return Array.from(groupMap.values());
  }, [selectedAnimation, gameObjects]);

  // Collect all unique keyframe times for summary row
  const allKeyframeTimes = useMemo(() => {
    if (!selectedAnimation) return [];

    const times = new Set<number>();
    selectedAnimation.tracks.forEach((track) => {
      track.keyframes.forEach((kf) => times.add(kf.time));
    });

    return Array.from(times).sort((a, b) => a - b);
  }, [selectedAnimation]);

  // Calculate timeline width
  const timelineWidth = useMemo(() => {
    if (!containerRef.current) return 800;
    return containerRef.current.clientWidth - trackHeaderWidth;
  }, [trackHeaderWidth]);

  // Calculate playhead position
  const playheadX = currentTime * pixelsPerSecond - scrollLeft;

  // Handle box selection
  const handleTimelineMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    if (!timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;

    setIsBoxSelecting(true);
    setBoxSelection({ startX, startY, endX: startX, endY: startY });

    // Clear selection if not holding modifier keys
    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
      clearKeyframeSelection();
    }
  }, [clearKeyframeSelection]);

  const handleTimelineMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isBoxSelecting || !timelineRef.current || !boxSelection) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const endX = e.clientX - rect.left;
    const endY = e.clientY - rect.top;

    setBoxSelection((prev) => prev ? { ...prev, endX, endY } : null);
  }, [isBoxSelecting, boxSelection]);

  const handleTimelineMouseUp = useCallback(() => {
    if (!isBoxSelecting || !boxSelection || !selectedAnimation) {
      setIsBoxSelecting(false);
      setBoxSelection(null);
      return;
    }

    // Calculate time range from box selection
    const minX = Math.min(boxSelection.startX, boxSelection.endX);
    const maxX = Math.max(boxSelection.startX, boxSelection.endX);

    const startTime = (minX + scrollLeft) / pixelsPerSecond;
    const endTime = (maxX + scrollLeft) / pixelsPerSecond;

    // Determine which tracks are in the Y range
    const minY = Math.min(boxSelection.startY, boxSelection.endY);
    const maxY = Math.max(boxSelection.startY, boxSelection.endY);

    // Calculate row positions
    let currentY = SUMMARY_ROW_HEIGHT; // Start after summary row
    const trackIdsInRange: string[] = [];

    trackGroups.forEach((group) => {
      group.tracks.forEach((track) => {
        const rowTop = currentY;
        const rowBottom = currentY + ROW_HEIGHT;

        if (rowBottom > minY && rowTop < maxY) {
          trackIdsInRange.push(track.id);
        }
        currentY += ROW_HEIGHT;
      });
    });

    // Select keyframes in range
    selectKeyframesInRange(startTime, endTime, trackIdsInRange.length > 0 ? trackIdsInRange : undefined);

    setIsBoxSelecting(false);
    setBoxSelection(null);
  }, [isBoxSelecting, boxSelection, selectedAnimation, scrollLeft, pixelsPerSecond, trackGroups, selectKeyframesInRange]);

  // Handle keyframe click
  const handleKeyframeClick = useCallback((keyframeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const addToSelection = e.shiftKey || e.ctrlKey || e.metaKey;
    selectKeyframe(keyframeId, addToSelection);
  }, [selectKeyframe]);

  // Render a keyframe diamond
  const renderKeyframeDiamond = useCallback((
    keyframeId: string,
    time: number,
    color: string,
    isSummary: boolean = false
  ) => {
    const x = time * pixelsPerSecond - scrollLeft;
    const isSelected = selectedKeyframeIds.includes(keyframeId);

    // Check if visible in viewport
    if (x < -DIAMOND_SIZE || x > timelineWidth + DIAMOND_SIZE) return null;

    return (
      <div
        key={keyframeId}
        className={cn(
          'absolute top-1/2 -translate-y-1/2 transform rotate-45 cursor-pointer',
          'transition-transform hover:scale-125',
          isSelected && 'scale-110',
          isSummary && 'opacity-60'
        )}
        style={{
          left: x - DIAMOND_SIZE / 2,
          width: DIAMOND_SIZE,
          height: DIAMOND_SIZE,
          backgroundColor: isSelected ? '#ffffff' : color,
          border: `1.5px solid ${isSelected ? '#ffffff' : color}`,
        }}
        onClick={(e) => handleKeyframeClick(keyframeId, e)}
      />
    );
  }, [pixelsPerSecond, scrollLeft, selectedKeyframeIds, timelineWidth, handleKeyframeClick]);

  // Handle document-level mouse events for box selection
  useEffect(() => {
    if (!isBoxSelecting) return;

    const handleMouseUp = () => {
      handleTimelineMouseUp();
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [isBoxSelecting, handleTimelineMouseUp]);

  if (!selectedAnimation) {
    return (
      <div className={cn('flex items-center justify-center h-full text-zinc-500 text-sm', className)}>
        Select an animation to view the dope sheet
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-col bg-zinc-900 overflow-hidden', className)}
    >
      {/* Summary Row */}
      <div className="flex border-b border-zinc-700" style={{ height: SUMMARY_ROW_HEIGHT }}>
        {/* Summary header */}
        <div
          className="flex items-center px-3 bg-zinc-850 border-r border-zinc-700"
          style={{ width: trackHeaderWidth, minWidth: trackHeaderWidth }}
        >
          <span className="text-xs font-medium text-zinc-300">Summary</span>
        </div>

        {/* Summary timeline */}
        <div className="relative flex-1 bg-zinc-850/50 overflow-hidden">
          {allKeyframeTimes.map((time, index) => (
            <div
              key={`summary-${index}`}
              className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-zinc-500"
              style={{ left: time * pixelsPerSecond - scrollLeft - 3 }}
            />
          ))}

          {/* Playhead */}
          {playheadX >= 0 && playheadX <= timelineWidth && (
            <div
              className="absolute top-0 bottom-0 w-px bg-red-500 pointer-events-none z-10"
              style={{ left: playheadX }}
            />
          )}
        </div>
      </div>

      {/* Scrollable track area */}
      <div className="flex-1 overflow-auto">
        <div className="flex">
          {/* Track headers column */}
          <div
            className="flex flex-col flex-shrink-0 border-r border-zinc-700"
            style={{ width: trackHeaderWidth, minWidth: trackHeaderWidth }}
          >
            {trackGroups.map((group) => (
              <div key={group.objectId}>
                {group.tracks.map((track) => {
                  const propertyLabel = PROPERTY_LABELS[track.property] || track.property;

                  return (
                    <div
                      key={track.id}
                      className="flex items-center gap-2 px-3 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors"
                      style={{ height: ROW_HEIGHT }}
                    >
                      {/* Color indicator */}
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: track.color }}
                      />

                      {/* Object and property name */}
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-zinc-500 truncate">{group.objectName}</div>
                        <div className="text-xs text-zinc-300 truncate -mt-0.5">{propertyLabel}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Timeline area */}
          <div
            ref={timelineRef}
            className={cn(
              'relative flex-1',
              isBoxSelecting && 'cursor-crosshair'
            )}
            onMouseDown={handleTimelineMouseDown}
            onMouseMove={handleTimelineMouseMove}
            onMouseUp={handleTimelineMouseUp}
          >
            {/* Track rows */}
            {trackGroups.map((group) => (
              <div key={group.objectId}>
                {group.tracks.map((track) => (
                  <div
                    key={track.id}
                    className="relative border-b border-zinc-800"
                    style={{ height: ROW_HEIGHT }}
                  >
                    {/* Keyframes */}
                    {track.keyframes.map((keyframe) =>
                      renderKeyframeDiamond(keyframe.id, keyframe.time, track.color)
                    )}

                    {/* Playhead line */}
                    {playheadX >= 0 && playheadX <= timelineWidth && (
                      <div
                        className="absolute top-0 bottom-0 w-px bg-red-500 pointer-events-none z-10"
                        style={{ left: playheadX }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Box selection overlay */}
            {isBoxSelecting && boxSelection && (
              <div
                className="absolute border border-blue-500 bg-blue-500/20 pointer-events-none z-20"
                style={{
                  left: Math.min(boxSelection.startX, boxSelection.endX),
                  top: Math.min(boxSelection.startY, boxSelection.endY),
                  width: Math.abs(boxSelection.endX - boxSelection.startX),
                  height: Math.abs(boxSelection.endY - boxSelection.startY),
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DopeSheet;
